// One file's diff on the review screen, drawn like an editor's: syntax colours, bracket
// pairs by depth, rainbow indent guides, ligatures (state/codeLook.ts). Lines do not wrap:
// the table scrolls sideways as one, so a long line keeps its shape on a phone. The
// lexer loads on demand; until then, and past the size limit, the lines are plain text.
import { useMemo } from 'react'
import type { ReviewFile } from '@/db/api'
import { CodeLine, useCodeStyle, useHighlighter } from '@/components/Code'
import { indentsOf } from '@/lib/syntax/indent'
import { useCodeLook } from '@/state/codeLook'
import { cx } from '@/components/ui'

export const DiffFile = ({ file }: { file: ReviewFile }) => {
  const on = useCodeLook((s) => s.highlight)
  const guides = useCodeLook((s) => s.guides)
  const hl = useHighlighter(on && !file.binary)
  const { style, data } = useCodeStyle()
  const texts = useMemo(() => file.hunks.flatMap((h) => h.lines.map((l) => l.text)), [file])
  const big = !!hl && hl.tooBig(texts)
  const lang = hl && on ? hl.langOfPath(file.path) : undefined
  const toks = useMemo(
    () => (hl && lang && !big ? hl.highlightDiff(lang, file.hunks) : undefined),
    [hl, lang, big, file],
  )
  const ind = useMemo(() => indentsOf(texts), [texts])
  if (file.binary) return <p className="text-xs text-faint px-3 py-2">A binary file.</p>
  let row = 0
  return (
    <div className="code code-scroll" style={style} {...data} data-testid="review-diff">
      <table className="code-table text-[12px] leading-[1.45]">
        <tbody>
          {file.hunks.map((h, i) => [
            <tr key={`h${i}`}>
              <td colSpan={3} className="px-3 py-1 text-faint bg-raised/60 select-none">
                {h.header}
              </td>
            </tr>,
            ...h.lines.map((l, j) => {
              const at = row++
              return (
                <tr key={`${i}-${j}`} className={cx(l.kind === '+' && 'diff-add', l.kind === '-' && 'diff-del')}>
                  <td className="gut w-10 pr-1 text-right text-faint select-none align-top">{l.old ?? ''}</td>
                  <td className="gut w-10 pr-2 text-right text-faint select-none align-top">{l.new ?? ''}</td>
                  <td className="pr-4 whitespace-pre">
                    <span className="select-none text-faint">{l.kind === ' ' ? ' ' : l.kind}</span>
                    <CodeLine
                      text={l.text}
                      toks={toks?.[i]?.[j]}
                      unit={ind.unit}
                      level={ind.levels[at]!}
                      guides={guides}
                    />
                  </td>
                </tr>
              )
            }),
          ])}
        </tbody>
      </table>
      {big && <p className="text-xs text-faint px-3 py-2">Highlighting is off for this large file.</p>}
      {file.truncated && <p className="text-xs text-faint px-3 py-2">The rest of this file is on Forgejo.</p>}
    </div>
  )
}
